import { useEffect } from 'react'
import { ATTR } from '@terramantle/telemetry'
import { trackEvent, trackTiming } from '@/lib/telemetry'

/**
 * Modal telemetry: every dialog in the portal reports that it opened, and how long it stayed open.
 *
 * ── Why a closed union and not the modal's title ────────────────────────────────────────────────
 *
 * The obvious implementation - emit `title` - is the one thing that must not happen. Real titles in
 * this app include `` `${t.name}` `` (a bot token's label), `workspace.name`, `` `Delete ${m.name}` ``
 * (a module), `` `${namespace}/${type}` `` and `org.slug`. Those are customer-authored strings, which
 * is exactly what §16 keeps out of `event.target`, and they are unbounded, which is what §17 keeps
 * out of a groupable attribute. The same reasoning already governs `[data-track-event]`: the target
 * is read only from an attribute someone deliberately set, never from what the element says.
 *
 * So the name is a literal from the union below, `Modal`'s `track` prop is REQUIRED, and a new modal
 * is a compile error until it is named here. That makes the coverage question - "is every modal
 * instrumented?" - answerable by the type checker rather than by grepping, and makes the privacy
 * review a review of this one list.
 *
 * ── What gets emitted ───────────────────────────────────────────────────────────────────────────
 *
 *  - open:  a `ui.event` log record + short span, `event.type=modal_open`, `event.target=<name>`.
 *           It rides the existing event path, so it carries `app.path`, `app.route`, the journey and
 *           the identity attributes with no new plumbing, and the existing `ui.event` queries pick
 *           modals up for free.
 *  - close: a `ui.modal.view` SPAN whose duration is how long the dialog was open, backdated via
 *           `trackTiming` exactly as `auth.bootstrap` is. Dwell is real duration and therefore a
 *           span (§8); holding a span open across the mount would hit the same "span that never
 *           ends" problem §10 describes for navigation. Keyed on the same `event.target`, so one
 *           filter (`event.target = "coder_templates"`) finds both the opens and the dwells.
 *
 * There is deliberately no `modal_close` log record: the open log already counts opens, and the span
 * already carries the close. A second log record per modal would double the volume for nothing.
 */
export type ModalName =
  // Shell, nav and the "coming soon" interest dialogs (Layout)
  | 'welcome'
  | 'notifications_coming_soon'
  | 'coder_templates'
  | 'mcp_server'
  | 'drift_detection'
  | 'audit'
  | 'plans_intro'
  | 'demo_join_success'
  | 'beta_enterprise_upgrade'
  | 'beta_feedback'
  | 'support'
  | 'access_tokens'
  // Org, onboarding and billing
  | 'create_org_wizard'
  | 'invite_org_member'
  | 'org_trust_repo'
  | 'vcs_trust_add'
  | 'vcs_trust_edit_refs'
  | 'org_leave_confirm'
  | 'org_delete_confirm'
  | 'org_consumption_block_arm'
  | 'org_consumption_block_disarm'
  | 'org_upgrade_plan'
  // Policy
  | 'policy_input_help'
  | 'policy_template_gallery'
  | 'policy_baseline_apply'
  | 'org_policy_edit'
  | 'org_policy_disable_confirm'
  | 'org_policy_delete_confirm'
  | 'policy_override_delete_confirm'
  | 'module_version_policies'
  | 'provider_policies'
  // Policies -> Audit Log "what this implies" help panels
  | 'help_policy_refusals_daily'
  | 'help_policy_top_policies'
  | 'help_policy_ledger'
  // Modules
  | 'publish_module'
  | 'module_deprecate'
  | 'module_undeprecate'
  | 'module_version_deprecate'
  | 'module_version_undeprecate'
  | 'module_block'
  | 'module_unblock'
  | 'module_policy_override'
  | 'module_delete_confirm'
  // Dependency graph
  | 'graph_lock_file_helper'
  // NOTE: edge inspection used to be here as `graph_edge_detail`. It is a hover card now, not a
  // dialog, and hover is not an intent the way opening a dialog is - instrumenting it would emit
  // an event for every line the cursor rested on while reading the canvas. Removed rather than
  // left dangling, because this list IS the privacy review: a name nothing emits reads as a
  // surface someone forgot to check.
  // State and plans
  | 'state_workspace_detail'
  | 'workspace_backend_config'
  | 'state_resource_inspector'
  | 'state_promote_confirm'
  | 'state_force_unlock_confirm'
  | 'state_resource_remove_confirm'
  // Providers
  | 'provider_artefact_detail'
  | 'provider_consume_code'
  | 'manage_provider'
  | 'provider_remove_confirm'
  | 'provider_serve_override_confirm'
  | 'provider_serve_block_confirm'
  // Tokens
  | 'create_token'
  | 'bot_token_detail'
  | 'bot_token_secret_rotated'
  | 'bot_token_rotate_confirm'
  | 'bot_token_delete_confirm'
  // Dashboard "what this implies" help tooltips (hover-opened; mount/unmount still
  // maps to open/close, so the open count and dwell span work unchanged)
  | 'help_risk_all_clear'
  | 'help_downloads_trend'
  | 'help_state_pushes_trend'
  | 'help_state_scan_findings'
  | 'help_module_scan_findings'
  | 'help_patch_adoption'
  | 'help_resources_at_a_glance'
  | 'help_highest_risk_modules'
  | 'help_highest_risk_workspaces'
  | 'help_slowest_applies'
  | 'help_push_cadence'

/** `event.type` for the open event. A closed value, in the snake_case enum style of §19. */
export const MODAL_OPEN_EVENT = 'modal_open'

/** Span name for the dwell. A fixed string - never interpolated with the modal name (§19). */
export const MODAL_VIEW_SPAN = 'ui.modal.view'

export function modalOpened(name: ModalName): void {
  trackEvent(MODAL_OPEN_EVENT, name)
}

/**
 * The dwell span, emitted on close.
 *
 * `openedAt` is a `Date.now()` taken at mount. Clamped at zero by `trackTiming` itself, so a clock
 * that jumps backwards mid-dialog produces a zero-length span rather than a span that ends before it
 * starts.
 */
export function modalClosed(name: ModalName, openedAt: number, now: number = Date.now()): void {
  trackTiming(MODAL_VIEW_SPAN, now - openedAt, { [ATTR.EVENT_TARGET]: name })
}

/**
 * Bind the open/close pair to a component's lifetime.
 *
 * `Modal` conditionally renders at every call site - mount IS open and unmount IS close, which the
 * component's own comment already relies on for `showModal()`. So one mount effect covers both
 * halves, including the paths that are easy to miss by instrumenting the trigger instead: Escape,
 * a backdrop click, and an unmount caused by navigating away with the dialog still open.
 *
 * The empty dependency list is the point: `name` is a literal at every call site and must not be
 * able to re-fire this. StrictMode's dev double-mount therefore produces open / ~0ms dwell / open;
 * that is dev-only, and dev has no `VITE_OTEL_INGEST_TOKEN`, so the gateway drops those records
 * anyway. A ref guard is deliberately NOT used: a genuine re-mount IS a second open, and suppressing
 * it would lose every re-open of the same dialog.
 */
export function useModalTelemetry(name: ModalName): void {
  useEffect(() => {
    const openedAt = Date.now()
    modalOpened(name)
    return () => modalClosed(name, openedAt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
